/* import __COLOCATED_TEMPLATE__ from './conversation-excerpt-row.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type CopilotApi from 'embercom/services/copilot-api';
import { ContentSourceStatusType, ContentSourceType } from '../content';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type Store from '@ember-data/store';
import type ExternalAgent from 'embercom/models/standalone/external-agent';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';

interface Signature {
  Args: {
    conversationCount: number;
    onOpenConversationExcerptDrawer: () => void;
  };
}

export default class ConversationExcerptRow extends Component<Signature> {
  @service declare copilotApi: CopilotApi;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare store: Store;

  @tracked showManageConversationsDrawer = false;
  @tracked externalAgents: ExternalAgent[] = [];
  contentSourceType = ContentSourceType;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.fetchExternalAgents).perform();
  }

  @dropTask
  *fetchExternalAgents() {
    let agents: ExternalAgent[] = yield this.store.findAll('standalone/external-agent');
    this.externalAgents = agents.toArray().sortBy('name');
  }

  get conversationExcerptIcon() {
    return this.appService.mainStandalonePlatform.icon;
  }

  get conversationExcerptTitle() {
    return (
      this.appService.mainStandalonePlatform.componentData['conversation-excerpt']?.title(
        this.excerptsEnabled,
      ) ?? ''
    );
  }

  get conversationExcerptLink() {
    return this.appService.mainStandalonePlatform.componentData['conversation-excerpt']?.link;
  }

  get conversationExcerptStatus() {
    if (this.excerptsEnabled) {
      if (this.hasSufficientConversationData) {
        return ContentSourceStatusType.EnabledForCopilot;
      } else {
        return ContentSourceStatusType.NotSufficientConversationExcerpts;
      }
    } else {
      return ContentSourceStatusType.NotEnabled;
    }
  }

  private get hasSufficientConversationData() {
    return this.args.conversationCount > 10;
  }

  get allAdmins() {
    return this.externalAgents.map((externalAgent: ExternalAgent) => ({
      id: Number(externalAgent.id),
      name: externalAgent.name,
    }));
  }

  get enabledAdmins() {
    return this.copilotApi.copilotSettings?.trusted_admin_ids ?? [];
  }

  get excerptsEnabled() {
    return this.copilotApi.copilotSettings?.use_past_conversations ?? true;
  }

  get conversationAttributes() {
    return this.copilotApi.copilotSettings?.excerpt_matching_attributes;
  }

  @action async onManageConversationExcerpts() {
    try {
      await this.knowledgeHubService.ensurePermissions();
    } catch (e) {
      return;
    }
    this.showManageConversationsDrawer = true;
  }

  @action
  closeDrawer() {
    this.copilotApi.refreshCopilotSettingsData();
    this.showManageConversationsDrawer = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Knowledge::Standalone::ConversationExcerptRow': typeof ConversationExcerptRow;
  }
}
